import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { SYS5020_R01Dto, defaultSYS5020_R01Dto } from './Dto_SYS5020_R01';
import { useSYS5020_R01Store } from './Store_SYS5020_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellStringComparator, cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SYS5020_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS5020_R01Dto> | null>;
}

export const Table_SYS5020_R01 = ({ gridRef }: Props) => {
	const {
		SYS5020_R01List,
		selectedSYS5020_R01,
		setSelectedSYS5020_R01,
		initSelectedSYS5020_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useSYS5020_R01Store();
	
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS5020_R01List,
		defaultGridData: defaultSYS5020_R01Dto,
		selectedRow: selectedSYS5020_R01,
	});

	const handleRowClick = useCallback((e: RowClickedEvent<SYS5020_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedSYS5020_R01?.valueIndex === rowData.valueIndex;
		setSelectedSYS5020_R01(isSelected ? undefined : rowData);
	}, [selectedSYS5020_R01, setSelectedSYS5020_R01]);

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'userId')(params), []);

	const baseColumnDefs: ColDef<SYS5020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '접속ID', colId: 'userId', field: 'userId', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'userId'),
			comparator: cellStringComparator,
		},
		{
			headerName: '접속자명', colId: 'userNm', field: 'userNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'userNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: '프로그램 ID', colId: 'programId', field: 'programId', width: 130,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'programId'),
			comparator: cellStringComparator,
		},
		{
			headerName: '프로그램 명', colId: 'programNm', field: 'programNm', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'programNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: '사용구분', colId: 'actionTypeNm', field: 'actionTypeNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'actionTypeNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: '로그인 시간', colId: 'logDt', field: 'logDt', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'logDt'),
			comparator: cellDateComparator,
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			comparator: cellStringComparator,
		},
	], [getCellStyle, cellStringComparator, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SYS5020_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SYS5020_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: {},
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		userId: `${SYS5020_R01List.length.toLocaleString()}건`,
		editable: false,
	} as unknown as SYS5020_R01Dto]), [SYS5020_R01List.length]);

	return (
		<GridContextWrapper<SYS5020_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS5020_R01}
			defaultRowData={defaultSYS5020_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SYS5020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SYS5020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS5020_R01?.valueIndex !== undefined
						? [selectedSYS5020_R01.valueIndex] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default Table_SYS5020_R01;
